import { useState, useEffect } from 'react';
import { Col, Container, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import CommonLogo from '../Common/CommonLogo';
import { Btn, H4, P } from '../../../../AbstractElements';
import { SignIn } from '../../../../utils/Constant';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthService from '../../../../Api/services/auth.service';
import { Eye, EyeOff } from 'react-feather';
import { useTranslation } from 'react-i18next';

const ResetPasswordContainer = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordId, setForgotPasswordId] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Obtener ID del estado de navegación o localStorage
    const state = location.state as { forgotPasswordId?: number } | null;
    const storedId = localStorage.getItem('forgotPasswordId');
    
    if (state?.forgotPasswordId) {
      setForgotPasswordId(state.forgotPasswordId);
    } else if (storedId) {
      setForgotPasswordId(parseInt(storedId));
    } else {
      // Si no hay ID, redirigir a forgot password
      toast.error(t("Sesión expirada. Por favor solicita un nuevo código"));
      navigate(`${process.env.PUBLIC_URL}/authentication/forgetpassword`);
    }
  }, [location, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      toast.error(t("Por favor ingresa una contraseña"));
      return;
    }

    if (password.length < 8) {
      toast.error(t("La contraseña debe tener al menos 8 caracteres"));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("Las contraseñas no coinciden"));
      return;
    }

    if (!forgotPasswordId) {
      toast.error(t("Error: ID de recuperación no encontrado"));
      return;
    }

    setLoading(true);
    try {
      await AuthService.resetPassword(forgotPasswordId, password);
      
      // Limpiar datos de recuperación
      localStorage.removeItem('forgotPasswordId');
      
      toast.success(t("Contraseña cambiada exitosamente. Puedes iniciar sesión ahora."));
      
      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        navigate(`${process.env.PUBLIC_URL}/login`);
      }, 2000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("Error al cambiar la contraseña");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <Container fluid className="p-0">
        <Row>
          <Col xs="12">
            <div className="login-card login-dark">
              <div>
                <div>
                  <CommonLogo />
                </div>
                <div className="login-main">
                  <Form className="theme-form" onSubmit={handleSubmit}>
                    <H4>{t("CreateYourPassword")}</H4>
                    <P className="text-muted mb-4">
                      {t("Ingresa tu nueva contraseña")}
                    </P>
                    <FormGroup>
                      <Label className="col-form-label">{t("NewPassword")}</Label>
                      <div className="form-input position-relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          required
                          placeholder="*********"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={loading}
                          minLength={8}
                        />
                        <div className="show-hide" onClick={() => setShowPassword(!showPassword)}>
                          <span>
                            {showPassword ? (
                              <EyeOff size={18} color="var(--theme-default)" />
                            ) : (
                              <Eye size={18} color="var(--theme-default)" />
                            )}
                          </span>
                        </div>
                      </div>
                      <small className="text-muted">{t("Mínimo 8 caracteres")}</small>
                    </FormGroup>
                    <FormGroup>
                      <Label className="col-form-label">{t("RetypePassword")}</Label>
                      <div className="form-input position-relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          placeholder="*********"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={loading}
                          minLength={8}
                        />
                        <div className="show-hide" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                          <span>
                            {showConfirmPassword ? (
                              <EyeOff size={18} color="var(--theme-default)" />
                            ) : (
                              <Eye size={18} color="var(--theme-default)" />
                            )}
                          </span>
                        </div>
                      </div>
                    </FormGroup>
                    {password !== confirmPassword && confirmPassword && (
                      <div className="text-danger mb-3">
                        <small>{t("Las contraseñas no coinciden")}</small>
                      </div>
                    )}
                    <FormGroup className="mb-0">
                      <Btn
                        color="primary"
                        block
                        className="w-100"
                        disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                        type="submit"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t("Cambiando contraseña...")}
                          </>
                        ) : (
                          t("Cambiar Contraseña")
                        )}
                      </Btn>
                    </FormGroup>
                    <P className="mt-4 mb-0 text-center">
                      {t("¿Ya tienes una cuenta?")}
                      <Link className="ms-2" to={`${process.env.PUBLIC_URL}/login`}>
                        {t("SignIn")}
                      </Link>
                    </P>
                  </Form>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPasswordContainer;
